import { PersonType, UserType } from '@api';
import { AddressFields, ContactFields, FormActions } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { SelectField } from '@hypeit-gmbh/hypeit-ui';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ColorInputField,
  PersonalInformationFields,
  UserOption,
} from './components';
import {
  PrivateInformationFormSchemaType,
  usePrivateInformationFormSchema,
  useUpdatePrivateInformation,
} from './hooks';

const TRANS_PATH = 'PRIVATE_INFORMATION.EDIT_PRIVATE_INFORMATION_FORM';

type EditPrivateInformationPropsType = {
  personDetails: PersonType;
  onClose: () => void;
  users: UserType[];
};

export const EditPrivateInformationForm: React.FC<
  EditPrivateInformationPropsType
> = ({ personDetails, onClose, users }) => {
  const { t } = useTranslation('personDetailsGeneral');

  const { updatePersonPrivateInformation } = useUpdatePrivateInformation({
    personId: personDetails.id,
    onSuccessCallback: onClose,
  });

  const { schema, defaultValues } =
    usePrivateInformationFormSchema(personDetails);
  const form = useForm<PrivateInformationFormSchemaType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onCancel = () => {
    form.reset(defaultValues);
    onClose();
  };

  const handleSubmit = async (data: PrivateInformationFormSchemaType) => {
    await updatePersonPrivateInformation({
      formData: data,
    });
  };

  const userOptions = users
    .map((user) => ({
      value: user.id,
      label: `${user.lastname}, ${user.firstname} - ${user.email}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="xl" data-testid="edit-private-information-form">
          <PersonalInformationFields />
          <ContactFields.Root>
            <ContactFields.Phone span={12} />
            <ContactFields.Mobile span={12} />
            <ContactFields.Email span={12} />
          </ContactFields.Root>
          <AddressFields.Root>
            <AddressFields.Street required={false} />
            <AddressFields.Addition required={false} />
            <AddressFields.Zip required={false} />
            <AddressFields.City required={false} />
            <AddressFields.Country required={false} />
          </AddressFields.Root>
          <Group>
            <ColorInputField
              label={t(`${TRANS_PATH}.COLOR_TOUR_REP_CALENDAR_LABEL`)}
              flex={1}
            />
            <SelectField
              label={t(`${TRANS_PATH}.USER_LABEL`)}
              fieldName="userId"
              data={userOptions}
              flex={1}
              clearable
              renderOption={(item) => <UserOption label={item.option.label} />}
              searchable
            />
          </Group>
          <FormActions
            onCancel={onCancel}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
